import React, { useRef } from 'react';
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '@/assets/styles/components/datepicker.styles.scss';
import { CustomDateRangeInput } from '@/components/datepicker/DatePickerInput.tsx';
import dayjs from 'dayjs';
import { tr } from 'date-fns/locale/tr';
import { If } from 'vite-plugin-react-control-statements';
import { useSizes } from '@/utility/responsive.ts';

interface BaseProps {
	className?: string;
	label?: string | null;
	minDate?: Date;
	maxDate?: Date;
	placeholderText?: string;
	placeholderLabel?: string;
	disabled?: boolean;
	dateFormat?: string;
	selectsRange?: boolean;
	onResetValue?: () => void;
	withPortal?: boolean;
	shouldCloseOnSelect?: boolean;
	monthsShown?: number;
	showTimeSelect?: boolean;
	showTimeSelectOnly?: boolean;
	timeIntervals?: number;
	timeCaption?: string;
	minTime?: Date;
	maxTime?: Date;
	portalId?: string;
	popperPlacement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
}

interface RangePickerProps extends BaseProps {
	startDate?: Date | null;
	endDate?: Date | null;
	onChange?: (dates: [Date | null, Date | null]) => void;
}

interface SinglePickerProps extends BaseProps {
	selectedDate?: Date | null;
	onChange?: (date: Date | null) => void;
}

interface CustomHeaderProps {
	date: Date;
	decreaseMonth: () => void;
	increaseMonth: () => void;
	changeMonth: (month: number) => void;
	changeYear: (year: number) => void;
	customHeaderCount: number;
	monthDate: Date | null;
}

type CustomDatePickerProps = RangePickerProps | SinglePickerProps;

const isRangePicker = (props: CustomDatePickerProps): props is RangePickerProps => {
	return props.selectsRange === true;
};

export const CustomDatePicker: React.FC<CustomDatePickerProps> = props => {
	registerLocale('tr', tr);
	setDefaultLocale('tr');
	const { isMobile } = useSizes();

	const { className = '', label, onResetValue, placeholderLabel } = props;

	const renderDayContents = (day: number): React.ReactNode => {
		return <span className="custom-day-content">{day}</span>;
	};

	const datePickerRef = useRef(null);

	const handleClose = () => {
		datePickerRef?.current?.setOpen(false);
	};

	const renderCustomHeader = ({ monthDate, date, decreaseMonth, increaseMonth, changeMonth, changeYear, customHeaderCount }: CustomHeaderProps) => {
		const onResetMonth = () => {
			const today: Date = dayjs().toDate();

			changeMonth(today.getMonth());
			changeYear(today.getFullYear());
		};

		if (customHeaderCount !== 0) {
			return (
				<div className="custom-header">
					<div className="header-left">
						<span
							className="button-select-prev"
							onClick={decreaseMonth}>
							<i className="icons-chevron-left" />
						</span>
						<span className="text-date-mouth-year">
							<b>{dayjs(monthDate).format('MMMM')} </b>
							{dayjs(monthDate).format('YYYY')}
						</span>
						<span
							className="button-select-next"
							onClick={increaseMonth}>
							<i className="icons-chevron-right" />
						</span>
					</div>
				</div>
			);
		}
		return (
			<div className="custom-header">
				<div className="header-left">
					<span
						className="button-select-prev"
						onClick={decreaseMonth}>
						<i className="icons-chevron-left" />
					</span>
					<span className="text-date-mouth-year">
						<b>{dayjs(date).format('MMMM')} </b>
						{dayjs(date).format('YYYY')}
					</span>
					<span
						className="button-select-next"
						onClick={increaseMonth}>
						<i className="icons-chevron-right" />
					</span>
				</div>
				<div className="header-right">
					<span
						className="button-reset-value"
						onClick={onResetValue}>
						<i className="icons-trash-bin-minimalistic" />
					</span>
					<span
						className="button-reset-month"
						onClick={onResetMonth}>
						<i className="icons-calendar-mark" />
					</span>
					<If condition={isMobile}>
						<span
							className="button-mobile-close"
							onClick={() => handleClose()}>
							<i className="icons-close" />
						</span>
					</If>
				</div>
			</div>
		);
	};

	const getDatePickerProps = () => {
		if (isRangePicker(props)) {
			return {
				selectsRange: true as const,
				startDate: props.startDate,
				endDate: props.endDate,
				onChange: (dates: [Date | null, Date | null]) => props.onChange?.(dates)
			};
		}
		return {
			selected: props.selectedDate,
			onChange: (date: Date | null) => props.onChange?.(date)
		};
	};

	return (
		<>
			<div className={`custom-datepicker ${className}`}>
				<DatePicker
					monthsShown={props.monthsShown}
					withPortal={props.withPortal}
					portalId={props.portalId}
					popperPlacement={props.popperPlacement}
					dateFormat={props.dateFormat}
					placeholderText={props.placeholderText}
					disabled={props.disabled}
					showTimeSelect={props.showTimeSelect}
					showTimeSelectOnly={props.showTimeSelectOnly}
					timeIntervals={props.timeIntervals}
					timeCaption={props.timeCaption}
					minTime={props.minTime}
					maxTime={props.maxTime}
					locale="tr"
					shouldCloseOnSelect={props.shouldCloseOnSelect}
					showPopperArrow={false}
					renderCustomHeader={renderCustomHeader}
					renderDayContents={renderDayContents}
					minDate={props.minDate}
					maxDate={props.maxDate}
					ref={datePickerRef}
					customInput={<CustomDateRangeInput label={label} placeholderLabel={placeholderLabel} />}
					{...getDatePickerProps()}
				/>
			</div>
		</>
	);
};
